import { Flex, Checkbox, Badge, Button, Text, Dialog } from "@radix-ui/themes";
import { DataTable } from "./ui/data-table";
import Header from "@/shared/components/header";
import { SortingState, createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { FilterSection } from "./ui/filter-section";
import AssignmentActions from "./ui/assignment-actions";
import AssignmentConfirmDialog from "./ui/assignment-confirm-dialog";
import { Pagination } from "./ui/pagination";
import {
  useAssignmentFilters,
  usePagination,
  useAssignmentActions,
} from "./hooks";
import { useUrlSyncedState } from "./hooks/useUrlSyncedState";
import { useSelectionClearOnUrlChange } from "./hooks/useSelectionClearOnUrlChange";
import {
  useGetOrdersPaginatedQuery,
  Order,
  useStagesQuery,
} from "@/shared/api/lod-api";
import { useStaffAnnotatorsQuery } from "@/shared/api/gateway-api";
import { SOURCE_OPTIONS } from "@/shared/consts";
import { NavigationItems } from "@/shared/types/navigation";
import { Link, useParams } from "react-router-dom";
import {
  visibleColumnNames,
  formatDateTime,
  getInitialSorting,
  generateDynamicColumnNames,
} from "./lib/utils";
import { StageCell } from "./ui/stage-cell";
import { RejectionReasonCell } from "./ui/rejection-reason-cell";
import { AnomalyReportCell } from "./ui/anomaly-report-cell";
import { TABLE_COLUMNS } from "./lib/types";
import { AnnotationStageStatusesForm } from "@/features/annotation/ui/annotation-stage-statuses-dialog";

const columnHelper = createColumnHelper<Order>();

const AssignmentView = () => {
  const { pagination, paginationActions } = usePagination();
  const { filterState, filterHandlers, activeFilters } = useAssignmentFilters({
    onApplyFilters: () => paginationActions.goToPage(0),
    onClearFilters: () => paginationActions.goToPage(0),
  });

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const { searchParams } = useUrlSyncedState();
  const [sorting, setSorting] = useState<SortingState>(
    getInitialSorting(searchParams)
  );

  const { storeKey } = useParams();
  const { data: annotatorsData } = useStaffAnnotatorsQuery();
  const { data: stages } = useStagesQuery();

  const stageStatusOptions = useMemo(
    () => stages?.result?.statuses || [],
    [stages]
  );

  const annotatorOptions = useMemo(
    () =>
      annotatorsData?.results.map((annotator) => ({
        key: annotator.id,
        value: `${annotator.first_name} ${annotator.last_name}`,
      })) || [],
    [annotatorsData]
  );

  const allAnnotators = useMemo(
    () =>
      annotatorOptions.length > 0
        ? [{ key: "null", value: "None" }, ...annotatorOptions]
        : [{ key: "null", value: "None" }],
    [annotatorOptions]
  );

  const annotatorNames = useMemo(() => {
    const mapping: Record<string, string> = {};
    if (annotatorsData?.results) {
      annotatorsData.results.forEach((annotator) => {
        mapping[annotator.id] =
          `${annotator.first_name} ${annotator.last_name}`;
      });
    }
    return mapping;
  }, [annotatorsData]);

  const allColumnNames = useMemo(() => {
    const dynamicColumnNames = stages?.result?.stages
      ? generateDynamicColumnNames(stages.result.stages)
      : {};
    return { ...visibleColumnNames, ...dynamicColumnNames };
  }, [stages]);

  const handleDetailsClick = useCallback((order: Order) => {
    setSelectedOrder(order);
    setDialogOpen(true);
  }, []);

  const columns = useMemo(() => {
    if (!stages?.result?.stages) {
      return [
        columnHelper.display({
          id: TABLE_COLUMNS.SELECT,
          header: ({ table }) => (
            <Checkbox
              checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomePageRowsSelected() ? "indeterminate" : false)
              }
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all"
            />
          ),
          cell: ({ row }) => (
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={(value) => row.toggleSelected(!!value)}
              aria-label="Select row"
            />
          ),
          enableSorting: false,
          size: 40,
        }),
        columnHelper.accessor("id", {
          header: allColumnNames[TABLE_COLUMNS.ID],
          enableSorting: false,
          size: 380,
          cell: ({ row, cell }) => (
            <Flex justify="between">
              <Text className="line-clamp-1 break-all">{cell.getValue()}</Text>
              <Button asChild variant="soft" color="gray" size="1">
                <Link to={`/stores/${storeKey}/${row.original.id}`}>Open</Link>
              </Button>
            </Flex>
          ),
        }),
        columnHelper.accessor("time", {
          header: allColumnNames[TABLE_COLUMNS.TIME],
          size: 250,
          cell: ({ cell }) => formatDateTime(cell.getValue()),
        }),
        columnHelper.accessor("order_source", {
          header: allColumnNames[TABLE_COLUMNS.ORDER_SOURCE],
          enableSorting: false,
          size: 250,
        }),
        columnHelper.accessor("annotator_id", {
          header: allColumnNames[TABLE_COLUMNS.ANNOTATOR_ID],
          enableSorting: false,
          size: 320,
          cell: ({ cell }) => {
            const annotatorId = cell.getValue();
            if (!annotatorId) return "None";
            return annotatorNames[annotatorId] || annotatorId;
          },
        }),
        columnHelper.accessor("status", {
          header: allColumnNames[TABLE_COLUMNS.STATUS],
          enableSorting: false,
          size: 200,
          cell: ({ cell }) => {
            const status = cell.getValue();
            let color: "indigo" | "gray" = "gray";

            if (status.toLowerCase() === "finished") {
              color = "indigo";
            } else {
              color = "gray";
            }

            return (
              <Badge color={color} className="capitalize">
                {status || "Not started"}
              </Badge>
            );
          },
        }),
        columnHelper.display({
          id: TABLE_COLUMNS.ANOMALY_REPORT,
          header: allColumnNames[TABLE_COLUMNS.ANOMALY_REPORT],
          cell: ({ row }) => (
            <AnomalyReportCell
              value={row.original.anomaly_report || ""}
              onDetailsClick={() => handleDetailsClick(row.original)}
            />
          ),
          enableSorting: false,
          size: 200,
        }),
      ];
    }

    const baseColumns = [
      columnHelper.display({
        id: TABLE_COLUMNS.SELECT,
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() ? "indeterminate" : false)
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        size: 40,
      }),
      columnHelper.accessor("id", {
        header: allColumnNames[TABLE_COLUMNS.ID],
        enableSorting: false,
        size: 380,
        cell: ({ row, cell }) => (
          <Flex justify="between">
            <Text className="line-clamp-1 break-all">{cell.getValue()}</Text>
            <Button asChild variant="soft" color="gray" size="1">
              <Link to={`/stores/${storeKey}/${row.original.id}`}>Open</Link>
            </Button>
          </Flex>
        ),
      }),
      columnHelper.accessor("time", {
        header: allColumnNames[TABLE_COLUMNS.TIME],
        size: 180,
        cell: ({ cell }) => (
          <div className="truncate" title={formatDateTime(cell.getValue())}>
            {formatDateTime(cell.getValue())}
          </div>
        ),
      }),
      columnHelper.accessor("order_source", {
        header: allColumnNames[TABLE_COLUMNS.ORDER_SOURCE],
        enableSorting: false,
        size: 150,
        cell: ({ cell }) => (
          <div className="truncate" title={cell.getValue()}>
            {cell.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor("annotator_id", {
        header: allColumnNames[TABLE_COLUMNS.ANNOTATOR_ID],
        enableSorting: false,
        size: 200,
        cell: ({ cell }) => {
          const annotatorId = cell.getValue();
          const displayText = !annotatorId
            ? "None"
            : annotatorNames[annotatorId] || annotatorId;
          return (
            <div className="truncate" title={displayText}>
              {displayText}
            </div>
          );
        },
      }),
    ];

    const anomalyColumn = columnHelper.display({
      id: TABLE_COLUMNS.ANOMALY_REPORT,
      header: allColumnNames[TABLE_COLUMNS.ANOMALY_REPORT],
      cell: ({ row }) => (
        <AnomalyReportCell
          value={row.original.anomaly_report || ""}
          onDetailsClick={() => handleDetailsClick(row.original)}
        />
      ),
      enableSorting: false,
      size: 200,
    });

    const dynamicColumns = stages.result.stages.flatMap((stage) => [
      columnHelper.display({
        id: `stage_${stage.code}`,
        header: allColumnNames[`stage_${stage.code}`],
        cell: ({ row }) => {
          const stageStatus = row.original.stage_detail_statuses?.find(
            (s) => s.stage === stage.code
          );

          return (
            <StageCell
              stageStatus={stageStatus}
              availableStatuses={stages.result.statuses}
              onDetailsClick={() => handleDetailsClick(row.original)}
            />
          );
        },
        enableSorting: false,
        size: 150,
      }),
      columnHelper.display({
        id: `rejection_${stage.code}`,
        header: allColumnNames[`rejection_${stage.code}`],
        cell: ({ row }) => {
          const stageStatus = row.original.stage_detail_statuses?.find(
            (s) => s.stage === stage.code
          );

          return (
            <RejectionReasonCell
              value={stageStatus?.rejection_reason || ""}
              onDetailsClick={() => handleDetailsClick(row.original)}
            />
          );
        },
        enableSorting: false,
        size: 200,
      }),
    ]);

    return [...baseColumns, ...dynamicColumns, anomalyColumn];
  }, [allColumnNames, annotatorNames, storeKey, stages, handleDetailsClick]);

  const clearRowSelection = useCallback(() => {
    setRowSelection({});
  }, []);

  useSelectionClearOnUrlChange(clearRowSelection, filterState.isInitialized);

  // Inline the query logic instead of using a custom hook
  const queryArgs = useMemo(
    () => ({
      ...activeFilters,
      page: pagination.page + 1, // Convert 0-based to 1-based pagination
      limit: pagination.pageSize,
    }),
    [activeFilters, pagination.page, pagination.pageSize]
  );

  const {
    data,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    refetch: _refetch,
  } = useGetOrdersPaginatedQuery(queryArgs, {
    skip: !filterState.isInitialized,
  });

  const queryState = useMemo(
    () => ({
      data: data?.results || [],
      count: data?.count || 0,
      isLoading,
      isFetching,
      isSuccess,
      isError,
      error,
    }),
    [data, isLoading, isFetching, isSuccess, isError, error]
  );

  const handleSortingChange = useCallback(
    (newSorting: SortingState | ((old: SortingState) => SortingState)) => {
      const sortingState =
        typeof newSorting === "function" ? newSorting(sorting) : newSorting;

      if (!Array.isArray(sortingState)) {
        console.error("Invalid sorting state:", sortingState);
        return;
      }

      setSorting(sortingState);

      const ordering = sortingState
        .map((item) => (item.desc ? `-${item.id}` : item.id))
        .join(",");

      filterHandlers.updateOrdering(ordering);
    },
    [sorting, filterHandlers]
  );

  const handleApplyFilters = useCallback(() => {
    filterHandlers.applyFilters();
    filterHandlers.resetAssignmentState();
  }, [filterHandlers]);

  const handleClearFilters = useCallback(() => {
    filterHandlers.clearFilters();
    filterHandlers.resetAssignmentState();
  }, [filterHandlers]);

  const { assignmentState, assignmentActions } = useAssignmentActions(
    rowSelection,
    queryState.data,
    filterHandlers.markAssignmentOccurred
  );

  return (
    <>
      <Header
        hasWarning
        title={NavigationItems.LOD_ASSIGNMENT.name}
        exclude={[NavigationItems.LOD_ASSIGNMENT.id]}
      />
      <Flex direction="row">
        <main className="flex flex-1 flex-col h-full p-4 pr-0 w-[calc(100%-270px)]">
          <DataTable
            loading={queryState.isLoading || queryState.isFetching}
            isSuccess={queryState.isSuccess}
            title="LOD Order Assignment"
            data={queryState.data}
            visibleColumnNames={allColumnNames}
            totalCount={queryState.count}
            columns={columns}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            renderActions={
              <AssignmentActions
                rowSelection={rowSelection}
                tableData={queryState.data}
                selectedAssignee={assignmentState.selectedAssignee}
                setSelectedAssignee={assignmentActions.setSelectedAssignee}
                handleAssign={assignmentActions.handleAssign}
                loading={assignmentState.isAssigning}
                selectedOrdersCSV={assignmentState.selectedOrdersCSV}
              />
            }
            renderPagination={
              <Pagination
                currentPage={pagination.page}
                setCurrentPage={paginationActions.goToPage}
                totalPages={pagination.calculateTotalPages(queryState.count)}
                pageSize={pagination.pageSize}
                setPageSize={paginationActions.changePageSize}
              />
            }
            emptyTitle="No data"
            emptyDescription=""
          />
        </main>
        <FilterSection
          isFiltersApplied={filterState.isFiltersApplied}
          orderId={filterState.orderId}
          onOrderIdChange={filterHandlers.onOrderIdChange}
          sourceList={SOURCE_OPTIONS}
          source={filterState.source}
          onSourceChange={filterHandlers.onSourceChange}
          applyFilters={handleApplyFilters}
          clearFilters={handleClearFilters}
          assigneeList={allAnnotators}
          assignee={filterState.assignee}
          onAssigneeChange={filterHandlers.onAssigneeChange}
          fromDate={filterState.fromDate}
          toDate={filterState.toDate}
          onFromDateChange={filterHandlers.onFromDateChange}
          onToDateChange={filterHandlers.onToDateChange}
          hasAssignmentOccurred={filterState.hasAssignmentOccurred}
          stageStatusOptions={stageStatusOptions}
          stageJourneyAnnotation={filterState.stageJourneyAnnotation}
          stageTransactionAnnotation={filterState.stageTransactionAnnotation}
          stageTrackPersistenceAnnotation={
            filterState.stageTrackPersistenceAnnotation
          }
          onStageJourneyAnnotationChange={
            filterHandlers.onStageJourneyAnnotationChange
          }
          onStageTransactionAnnotationChange={
            filterHandlers.onStageTransactionAnnotationChange
          }
          onStageTrackPersistenceAnnotationChange={
            filterHandlers.onStageTrackPersistenceAnnotationChange
          }
        />
      </Flex>

      <AssignmentConfirmDialog
        isOpen={assignmentState.dialogOpen}
        setIsOpen={assignmentActions.setDialogOpen}
        ordersWithAssignee={assignmentState.ordersWithAssignee}
        onConfirm={assignmentActions.handleConfirmAssignment}
      />

      <Dialog.Root open={dialogOpen} onOpenChange={setDialogOpen}>
        <Dialog.Content maxWidth="702px" aria-describedby={undefined}>
          {selectedOrder && (
            <AnnotationStageStatusesForm
              order={selectedOrder}
              onClose={() => setDialogOpen(false)}
            />
          )}
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
};

export default AssignmentView;
